import { RouterContextProvider } from 'react-router';

import type { UseFetchFnMeta } from '#/hooks/useFetchFn';
import { useFetchUrl } from '#/hooks/useFetchUrl';
import type { FetchUrlOptions } from '#/hooks/useFetchUrl';
import { clientSafeServerRouterContext } from '#/horizon/router-contexts/client-safe-server-router-context';
import { isRenderingServer } from '#/rendering/state/renderingState';
import { getJson } from '#/utils/http/getJson';
import { postJson } from '#/utils/http/postJson';
import { log } from '#/utils/logging';

type FetchEndpointUrlFactory<TArgs extends unknown[], TUrl extends string> = (
  ...args: TArgs
) =>
  | TUrl
  | {
      path: string;
      searchParams: Record<
        string,
        string | string[] | undefined | boolean | null
      >;
    }
  | undefined;

type FetchEndpointOptionsFactory<TArgs extends unknown[]> = (
  ...args: TArgs
) => FetchUrlOptions;

type FetchEndpointResponseMapper<TMappedResponse, TResponse> = (
  response: TResponse,
) => TMappedResponse;

export class FetchEndpoint<
  TArgs extends unknown[],
  TUrl extends string,
  TMappedResponse,
  TResponse,
> {
  urlFactory: FetchEndpointUrlFactory<TArgs, TUrl>;

  optionsFactory: FetchEndpointOptionsFactory<TArgs> | undefined;

  mapResponse: FetchEndpointResponseMapper<TMappedResponse, TResponse>;

  constructor(args: {
    urlFactory: FetchEndpointUrlFactory<TArgs, TUrl>;
    optionsFactory?: FetchEndpointOptionsFactory<TArgs>;
    mapResponse: FetchEndpointResponseMapper<TMappedResponse, TResponse>;
  }) {
    this.urlFactory = args.urlFactory;
    this.optionsFactory = args.optionsFactory;
    this.mapResponse = args.mapResponse;
  }

  url(...args: TArgs): string | undefined {
    const result = this.urlFactory(...args);
    if (!result || typeof result === 'string') return result;

    const searchParamsObject = new URLSearchParams();
    for (const [key, value] of Object.entries(result.searchParams)) {
      if (value !== false && value !== null && typeof value !== 'undefined') {
        if (Array.isArray(value)) {
          value.forEach((item) => searchParamsObject.append(key, item));
        } else {
          searchParamsObject.append(key, value as string);
        }
      }
    }

    const searchParamsString = searchParamsObject.toString();

    return searchParamsString
      ? `${result.path}?${searchParamsString}`
      : result.path;
  }

  client() {
    return async (...args: TArgs): Promise<TMappedResponse> => {
      const url = this.url(...args);
      const options = this.optionsFactory?.(...args);

      if (!url) throw new Error('Cannot fetch with undefined URL');

      // This is a horizon server request
      if (isRenderingServer()) {
        const message = `Attempting to use client-only fetch during SSR. Url ${url}`;

        // Besides throwing, also log so it's easier to see what's happening
        // Sometimes we try/catch these fetch calls to account for 404
        log.error(message);
        throw new Error(message);
      }

      const response = await (options?.method === 'post'
        ? postJson<TResponse>(url, options.body)
        : getJson<TResponse>(url));

      return this.mapResponse(response);
    };
  }

  server() {
    return ({
      request,
      context,
    }: {
      request: Request;
      context: Readonly<RouterContextProvider>;
    }) => {
      // We intentionally don't use getServerContext here to avoid importing server-only modules
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const serverContext = context.get(clientSafeServerRouterContext);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
      return serverContext.createServerFetchEndpointFn(this, {
        request,
        context,
      }) as (...args: TArgs) => Promise<TMappedResponse>;
    };
  }

  hook() {
    return (
      ...args: TArgs
    ): [TMappedResponse | undefined, UseFetchFnMeta<TMappedResponse>] => {
      const url = this.url(...args);
      const options = this.optionsFactory?.(...args);

      return useFetchUrl({
        url,
        options,
        mapResponse: this.mapResponse,
      });
    };
  }
}
